<script>
import { mapActions, mapGetters } from 'vuex';
import MarkingsListItem from '@/components/markings/list/MarkingsListItem.vue';
import { mapState } from 'vuex/dist/vuex.common.js';
import calcDuration from '@/utils/calcDuration';
import ExtendedRequestsTable from '@/components/requests/ExtendedRequestsTable';
import moment from 'moment';
import tinycolor from 'tinycolor2';

export default {
  name: 'PrintPallet',
  data() {
    return {
      initLoading: false,

      scannedScanCode: '',
      scannedScanCodes: [],
      fetchedScanCodes: [],
      fetchedLabelArchive: [],

      fetchingLabelArchiveParents: false,
      labelArchiveParents: [],
      preferredRequiredScanCount: 3,
      requiredScanCount: 3,
      parentModal: false,
      pendingPrintLabelSending: false,
      pendingPrintLabel: false,
      success: false,

      labelingPoints: [],
      labelingPointHistoryInterval: null,
      labelingPointHistory: [],
    };
  },
  components: {
    MarkingsListItem,
    ExtendedRequestsTable,
  },
  computed: {
    ...mapGetters([
      'globalSettings',
      'requestsTypes',
      'requestsStatuses',
      'selectedFilterAreaId',
      'selectedFilterLineId',
      'linesFromArea',
    ]),
    ...mapGetters('packingSettings', [
      'getLowerLevelUnit',
      'getHigherLevelUnit',
      'getUnitById',
    ]),
    ...mapGetters('debug', [
      'debug',
    ]),
    ...mapState({
      plantTimezone: state => state.settings.data?.plantTimeZone || null,
    }),
    parentUnits() {
      return this.fetchedLabelArchive.map(item => {
        const lowerUnit = this.getLowerLevelUnit(item.unitId, item.skuId);
        const higherUnit = this.getHigherLevelUnit(item.unitId, item.skuId);
        return {
          lowerUnit: lowerUnit || {},
          higherUnit: higherUnit || {},
        };
      });
    },
    skus() {
      return this.fetchedLabelArchive.map(item => item.skuId);
    },
    orderIds() {
      return this.fetchedLabelArchive.map(item => item.orderId);
    },
    areAllSkusTheSame() {
      return this.skus.every(sku => sku === this.skus[0]);
    },
    areAllOrderIdsTheSame() {
      return this.orderIds.every(orderId => orderId === this.orderIds[0]);
    },
    doesAllLabelArchiveHaveInBetweenUnits() {
      return this.parentUnits.every(item => item.lowerUnit.id && item.higherUnit.id);
    },
    rootParentUnitId() {
      if (this.fetchedLabelArchive.length === 0) return true;
      return (this.parentUnits || [{}])[0]?.higherUnit?.id || null;
    },
    rootSkuId() {
      return this.skus[0] || null;
    },
    isRootParentUnitLast() {
      if (this.fetchedLabelArchive.length === 0) return true;
      const higherUnit = this.getHigherLevelUnit(this.rootParentUnitId, this.rootSkuId);
      return !higherUnit;
    },
    areAllHigherUnitsTheSame() {
      return this.parentUnits.every(item => item.higherUnit.id === this.parentUnits[0].higherUnit.id);
    },
    areAllLowerUnitsTheSame() {
      return this.parentUnits.every(item => item.lowerUnit.id === this.parentUnits[0].lowerUnit.id);
    },
    allParentsIds() {
      return this.fetchedLabelArchive.flatMap(item => item.containers
        .filter(c => c.containerUnitId === this.rootParentUnitId)
        .map(c => c.containerId));
    },
    areAllParentsIdsTheSame() {
      if (this.allParentsIds.length === 0) return true;
      if (!this.areAllHigherUnitsTheSame) return false;
      return this.allParentsIds.every(id => id === this.allParentsIds[0]);
    },
    allUnits() {
      const units = this.fetchedLabelArchive.map(item => item.unitId);
      return [...new Set(units)];
    },
    allUnitsNames() {
      return this.allUnits.map(id => this.getUnitById(id).name);
    },
    isButtonWithoutErrors() {
      return this.areAllSkusTheSame
        && this.areAllOrderIdsTheSame
        && this.rootParentUnitId
        && this.areAllHigherUnitsTheSame
        && this.areAllLowerUnitsTheSame
        && this.areAllParentsIdsTheSame
        && this.doesAllLabelArchiveHaveInBetweenUnits
        && this.isRootParentUnitLast
        && this.hasLabelingPointPalletizing
        && this.isGoodParentStatusActive;
    },
    isAllOk() {
      return this.isButtonWithoutErrors
        && this.scannedScanCodes.length >= this.requiredScanCount;
    },
    goodParent() {
      return (this.labelArchiveParents || []).find(item => item);
    },
    goodParentAmountOfChildren() {
      return this.goodParent?.containedLabels.length || 0;
    },
    isGoodParentStatusActive() {
      if (!this.goodParent) return true;
      return this.goodParent?.labelStatus === 'Active';
    },
    labelingPoint() {
      return (this.labelingPoints || [])
        .find(item =>
          item.unitId === this.goodParent?.unitId);
    },
    hasLabelingPointPalletizing() {
      if (!this.labelingPoint) return true;
      return this.labelingPoint?.palletizing;
    },
    labelingPointId() {
      return this.labelingPoint?.id;
    },
    columns() {
      return [
        {
          key: 'id',
          label: 'Id',
          width: '50px',
        },
        {
          key: 'typeProperties',
          label: this.$t('form.type'),
        },
        {
          key: 'lineName',
          label: this.$t('line.line'),
          groupable: true,
          class: 'text-left',
        },
        {
          key: 'labellingPointName',
          label: this.$t('labellingPoint.labellingPoints'),
          groupable: true,
          class: 'text-left',
        },
        {
          key: 'printerName',
          label: this.$t('printer.printer'),
          class: 'text-left',
        },
        {
          key: 'orderNumber',
          label: this.$t('order.orders'),
          groupable: true,
          class: 'text-left',
        },
        {
          key: 'skuNumber',
          label: 'SKU',
          groupable: true,
          class: 'text-left',
        },
        {
          key: 'labelName',
          label: this.$t('label.labels'),
          groupable: true,
          class: 'text-left',
        },
        {
          key: 'statusProperties',
          label: this.$t('general.statuses'),
        },
        {
          key: 'created',
          label: this.$t('date.startDate'),
          class: 'text-left',
        },
        {
          key: 'duration',
          label: this.$t('time.duration'),
          class: 'text-left',
        },
      ];
    },
    requestTypesList() {
      return this.requestsTypes.map(t => ({
        ...t,
        label: t.text,
        value: t.type,
        iconClass: t.icon,
        color: t.color,
      })) || [];
    },
    requestStatus() {
      return this.requestsStatuses?.map(s => ({
        ...s,
        label: s.text,
        value: s.status.toLowerCase(),
        iconClass: s.icon,
        color: s.color,
      })) || [];
    },
    requestsList() {
      const plantUtcOffset = moment.tz(this.plantTimezone).utcOffset();
      return (this.labelingPointHistory || []).map(x => ({
        ...x,
        typeProperties: this.typeProperties(x.requestType),
        statusProperties: this.statusProperties(x.requestStatus),
        duration: x.created && x.completed ? this.calculateDuration(x.created, x.completed) : '-',
        created: x.created
          ? moment.utc(x.created).add(plantUtcOffset, 'minutes').format('DD/MM/YYYY HH:mm:ss') : '-',
        completed: x.completed
          ? moment.utc(x.completed).add(plantUtcOffset, 'minutes').format('DD/MM/YYYY HH:mm:ss') : '-',
        skuNumber: x.skuNumber || '-',
        labelName: x.labelName || '-',
      }));
    },
  },
  watch: {
    goodParentAmountOfChildren(v) {
      if (v > 0 && v < this.preferredRequiredScanCount) {
        this.requiredScanCount = v;
      } else {
        this.requiredScanCount = this.preferredRequiredScanCount;
      }
    },
    scannedScanCodes: {
      handler(v) {
        if (v.length === 0) return;

        const scanCodesToFetch = this.scannedScanCodes.filter(code => !this.fetchedScanCodes.includes(code));

        this.fetchLabelArchive(scanCodesToFetch);

        this.fetchedScanCodes = [
          ...this.fetchedScanCodes,
          ...scanCodesToFetch,
        ];
      },
      deep: true,
      immediate: true,
    },
    fetchedLabelArchive: {
      handler(v) {
        this.fetchingLabelArchiveParents = true;
        this.fetchLabelArchiveParents(v);
        this.fetchingLabelArchiveParents = false;
      },
      deep: true,
      immediate: true,
    },
    areAllSkusTheSame(v) {
      if (!v) {
        this.$bvModal.msgBoxOk('Nie wszystkie zeskanowane elementy są z tego samego produktu.', {
          size: 'sm',
          centered: true,
          okTitle: 'OK',
        });
      }
    },
    areAllOrderIdsTheSame(v) {
      if (!v) {
        this.$bvModal.msgBoxOk('Nie wszystkie zeskanowane elementy są z tego samego zlecenia.', {
          size: 'sm',
          centered: true,
          okTitle: 'OK',
        });
      }
    },
    rootParentUnitId: {
      handler(v) {
        if (!v) {
          this.$bvModal.msgBoxOk('Nie znaleziono elementu nadrzędnego do zeskanowanych kodów.', {
            size: 'sm',
            centered: true,
            okTitle: 'OK',
          });
        }
      },
    },
    areAllHigherUnitsTheSame(v) {
      if (!v) {
        this.$bvModal.msgBoxOk(`Nie wszystkie zeskanowane elementy mają tą samą jednostkę
        (${this.allUnitsNames.join(', ')}).`, {
          size: 'sm',
          centered: true,
          okTitle: 'OK',
        });
      }
    },
    doesAllLabelArchiveHaveInBetweenUnits(v) {
      if (!v) {
        this.$bvModal.msgBoxOk('Nie wszystkie zeskanowane elementy mają połączenia z paletami.', {
          size: 'sm',
          centered: true,
          okTitle: 'OK',
        });
      }
    },
    hasLabelingPointPalletizing(v) {
      if (!v) {
        this.$bvModal.msgBoxOk('Paletyzacja nie jest włączona na punkcie etykietowania.', {
          size: 'sm',
          centered: true,
          okTitle: 'OK',
        });
      }
    },
    isGoodParentStatusActive(v) {
      if (!v) {
        this.$bvModal.msgBoxOk('Paleta nie jest zamknięta.', {
          size: 'sm',
          centered: true,
          okTitle: 'OK',
        });
      }
    },
    labelingPointId() {
      this.labelingPointHistory = [];
      this.refreshLabelingPointHistory(true);
    },
    goodParent: {
      deep: true,
      immediate: true,
      async handler(v) {
        if (!v || !this.goodParent?.lineId) return;
        const { data } = await this.getLineLabelingPoint({
          params: {
            lineId: this.goodParent?.lineId,
          },
        });
        this.labelingPoints = data;
      },
    },
  },
  methods: {
    ...mapActions([
      'getRequests',
    ]),
    ...mapActions([
      'getLineLabelingPoint',
    ]),
    ...mapActions('labelArchive', [
      'getLabelArchiveByBarcodes',
      'getLabelArchiveByIds',
      'printPalletLabelArchive',
    ]),
    ...mapActions('packingSettings', [
      'getUnits',
      'getPackingSettings',
    ]),
    calculateDuration(start, end) {
      const timeDifference = new Date(end).getTime() - new Date(start).getTime();
      const duration = calcDuration(1, Math.round(timeDifference / 1000) + 1);
      if (timeDifference < 10) return `0,00${timeDifference} s`;
      if (timeDifference < 100) return `0,0${timeDifference} s`;
      if (timeDifference < 1000) return `0,${timeDifference} s`;
      return duration;
    },
    clearScanCodes() {
      this.scannedScanCode = '';
      this.scannedScanCodes = [];
      this.fetchedScanCodes = [];
      this.fetchedLabelArchive = [];
      this.labelArchiveParents = [];
      this.parentModal = false;
      this.pendingPrintLabel = false;
      this.labelingPointHistory = [];

      this.init();
    },
    typeProperties(type) {
      return this.requestsTypes.find(x => x.type?.toUpperCase() === type?.toUpperCase())
      || {
        type: 'UNKNOWN',
        icon: 'fas fa-exclamation-triangle',
        text: 'UNKNOWN',
        color: '#ff7448',
      };
    },
    statusProperties(status) {
      return this.requestsStatuses.map(x => ({
        ...x,
        backgroundColor: tinycolor(x.color).setAlpha(0.2).toString(),
      }))
        .find(x => x.status?.toUpperCase() === status?.toUpperCase());
    },
    async fetchLabelArchiveParents(labelArchive) {
      if (labelArchive.length === 0) return;
      if (this.allParentsIds.length === 0) return;

      this.loadingLabelArchiveParents = true;
      const { plantCode } = this.globalSettings;
      const { data } = await this.getLabelArchiveByIds({
        params: {
          plantCode,
          query: {
            ids: this.allParentsIds,
          },
        },
      });
      this.labelArchiveParents = data;

      this.loadingLabelArchiveParents = false;
    },
    async handleClickEnter() {
      const number = this.scannedScanCode;
      if (!number) return;
      this.scannedScanCode = '';

      if (this.scannedScanCodes.includes(number)) {
        this.$bvModal.msgBoxConfirm(`Ten kod "${number}" został już zeskanowany.`, {
          size: 'sm',
          buttonSize: 'md',
          okVariant: 'success',
          okTitle: 'OK',
          cancelTitle: 'Ok',
          centered: true,
        });
        return;
      }

      this.scannedScanCodes = [
        ...this.scannedScanCodes,
        number,
      ];
    },
    handleKeyDown(event) {
      if (this.parentModal) return;

      if (event.key === 'Enter' || event.key === 'NumpadEnter' || event.key === '\r') {
        this.handleClickEnter();
      } else {
        const isNumber = /^[0-9]$/;
        const isLetter = /^[A-Za-z]$/;
        const isSemiColon = /^;$/;
        const isSpecial = /^[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/;
        const isDash = /^-$/;
        if (
          isNumber.test(event.key)
          || isLetter.test(event.key)
          || isSemiColon.test(event.key)
          || isDash.test(event.key)
          || isSpecial.test(event.key)
        ) {
          this.scannedScanCode += event.key;
          event.preventDefault();
        }
      }
    },
    initKeyDownListener() {
      document.addEventListener(
        'keydown',
        this.handleKeyDown,
      );
    },
    removeKeyDownListener() {
      document.removeEventListener(
        'keydown',
        this.handleKeyDown,
      );
    },
    getFetchedLabelArchive(scanCode) {
      return this.fetchedLabelArchive.find(item => item.scanCode === scanCode);
    },
    async fetchLabelArchive(scanCodesToFetch) {
      if (scanCodesToFetch.length === 0) return;
      const { plantCode } = this.globalSettings;
      const labelArchive = await Promise.all(scanCodesToFetch.map(async serialNumber => {
        const { data } = await this.getLabelArchiveByBarcodes({
          params: {
            plantCode,
            query: {
              exactBarCodes: [serialNumber],
            },
          },
        });
        return data;
      }));

      const foundCodes = labelArchive.flatMap(item => item.map(i => i.scanCode));
      const notFoundCodes = scanCodesToFetch.filter(code => !foundCodes.includes(code));

      notFoundCodes.forEach(code => {
        this.$bvModal.msgBoxConfirm(`Kod "${code}" nie został znaleziony.`, {
          size: 'sm',
          buttonSize: 'md',
          okVariant: 'success',
          okTitle: 'OK',
          cancelTitle: 'OK',
          centered: true,
        });
      });
      this.scannedScanCodes = [
        ...this.scannedScanCodes.filter(code => !notFoundCodes.includes(code)),
      ];
      this.fetchedScanCodes = [
        ...this.fetchedScanCodes.filter(code => !notFoundCodes.includes(code)),
      ];

      this.fetchedLabelArchive = [
        ...this.fetchedLabelArchive,
        ...labelArchive.flatMap(item => item),
      ];
    },
    openParentModal() {
      if (this.debug) {
        this.parentModal = true;
        return;
      }

      if (!this.isAllOk) return;
      this.parentModal = true;
    },
    async printLabel() {
      this.pendingPrintLabel = true;
      this.pendingPrintLabelSending = true;
      const { plantCode } = this.globalSettings;
      await this.printPalletLabelArchive({
        params: {
          plantCode,
          id: this.goodParent.id,
        },
      });

      setTimeout(() => {
        this.pendingPrintLabelSending = false;
        this.pendingPrintLabel = true;

        setTimeout(() => {
          this.pendingPrintLabel = false;
          this.success = true;

          setTimeout(() => {
            this.success = false;
          }, 2000);
        }, 2000);
      }, 1000);
    },
    async getPrintRequests() {
      if (!this.labelingPointId) return;

      const { data } = await this.getRequests({
        params: {
          query: {
            types: ['Print'],
            page: 0,
            count: 20,
            labellingPointIds: [this.labelingPointId],
          },
        },
      });
      this.labelingPointHistory = (data?.items || [])
        .map(pr => ({
          ...pr,
          duration: calcDuration(moment.utc(pr.created).unix()),
        }));
    },
    async refreshLabelingPointHistory() {
      if (this.labelingPointHistoryInterval) {
        clearTimeout(this.labelingPointHistoryInterval);
      }

      if (!this.labelingPointId) return;

      await this.getPrintRequests();

      this.labelingPointHistoryInterval = setTimeout(() => {
        this.refreshLabelingPointHistory();
      }, 3000);
    },
    async init() {
      this.initLoading = true;
      const { plantCode } = this.globalSettings;
      await this.getUnits({
        params: {
          plantCode,
        },
      });
      await this.getPackingSettings({
        params: {
          plantCode,
        },
      });
      this.initLoading = false;
    },
  },
  async created() {
    this.init();
    this.initKeyDownListener();
  },
  beforeDestroy() {
    this.removeKeyDownListener();

    if (this.labelingPointHistoryInterval) {
      clearTimeout(this.labelingPointHistoryInterval);
    }
  },
};
</script>

<template>
  <Loader v-if="initLoading" />
  <div
    v-else
    class="print-pallet-cotainer"
  >
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h5 class="mb-0">
        <i class="fas fa-pallet-boxes" />
        Drukuj etykiety na palety
      </h5>

      <div>
        <BButton
          variant="secondary"
          @click="clearScanCodes"
        >
          <i class="fas fa-undo-alt mr-1" />
          Od początku
        </BButton>
      </div>
    </div>

    <div
      v-if="debug"
      class="card p-4 m-4"
    >
      <h6>
        DEBUG
      </h6>
      <input
        v-model="requiredScanCount"
        type="number"
        class="form-control"
      >
    </div>

    <div class="d-flex align-items-center py-4 border-top">
      <i class="fas fa-scanner-gun mr-2" />
      <input
        v-model="scannedScanCode"
        class="form-control"
        placeholder="Skanuj kody..."
      >
    </div>

    <div
      class="d-flex w-100 flex-grow-1 overflow-auto border-top pt-4"
      style="height: 50px"
    >
      <div class="scanned-codes">
        <div
          v-for="serialNumber in scannedScanCodes"
          :key="`found-${serialNumber}`"
        >
          <MarkingsListItem
            v-if="getFetchedLabelArchive(serialNumber)"
            :item="getFetchedLabelArchive(serialNumber)"
            is-compatibility-view
            class="fade-in"
            :loading-parents="fetchingLabelArchiveParents"
            :parents-list="labelArchiveParents"
            :is-compatibility-correct="isAllOk"
            non-interactive
            @select-item="$emit('select-item', $event)"
            @select-item-contents="$emit('select-item-contents', $event)"
          />
        </div>
      </div>
    </div>

    <div class="verification-result-container border-top">
      <div
        class="verification-result"
        :class="{
          'bg-light': fetchedScanCodes.length < requiredScanCount && isButtonWithoutErrors,
          'bg-success text-white success cursor-pointer': isButtonWithoutErrors
            && fetchedScanCodes.length >= requiredScanCount,
          'bg-danger text-white failed cursor-not-allowed': !isButtonWithoutErrors,
        }"
        @click="openParentModal"
      >
        <i
          v-if="fetchedScanCodes.length < requiredScanCount && isButtonWithoutErrors"
          class="fas fa-scanner-gun"
        />
        <i
          v-else-if="isAllOk"
          class="fas fa-print"
        />
        <i
          v-else
          class="fas fa-times-circle"
        />

        <div class="px-1" />

        <span v-if="fetchedScanCodes.length < requiredScanCount && isButtonWithoutErrors">
          Zeskanuj <strong>{{ requiredScanCount }}</strong> kody kartonów, które znajdują się na palecie.
        </span>
        <span
          v-else-if="isAllOk"
        >
          Przygotuj etykietę...
        </span>
        <span
          v-else
        >
          Nie można wydrukować etykiety, spróbuj ponownie.
        </span>
      </div>
    </div>

    <BModal
      id="modal-1"
      v-model="parentModal"
      title="Drukowanie etykiety na paletę..."
      size="xl"
      hide-footer
    >
      <h6>
        Historia drukowania:
      </h6>

      <div class="print-request-history-box">
        <ExtendedRequestsTable
          v-if="requestsList.length > 0"
          ref="requestTable"
          :list="requestsList"
          :table-fields="columns"
          no-selection
        />
      </div>

      <hr>

      <h5>
        Paleta:
      </h5>

      <MarkingsListItem
        :item="goodParent"
        :parents-list="[]"
        non-interactive
      />

      <BButton
        class="mt-5"
        variant="success"
        block
        :disabled="pendingPrintLabel || pendingPrintLabelSending"
        @click="printLabel"
      >
        <span v-if="!pendingPrintLabel && !pendingPrintLabelSending && !success">
          <i class="fas fa-print mr-1" />
          Drukuj etykietę
        </span>

        <span v-else-if="pendingPrintLabelSending">
          <i class="fas fa-spinner fa-spin" />
          Wysyłanie...
        </span>

        <span v-else-if="!success">
          <i class="fas fa-spinner fa-spin" />
          Drukowanie...
        </span>

        <span v-else>
          <i class="fas fa-check mr-1" />
          Etykieta została wydrukowana.
        </span>
      </BButton>
    </BModal>
  </div>
</template>

<style lang="scss" scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

.print-pallet-cotainer {
    width: 100%;
    height: calc(100% - 20px);
    background-color: white;
    padding: 20px;
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);

    .scan {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 80px;
        border: 1px solid #e9e9e9;
        border-radius: 5px;
        margin-bottom: 8px;

        .scanned-code {
            padding: 0 10px;
            font-size: 12px;
            height: 100%;
            inline-size: 100%;
            overflow-wrap: break-word;
        }
    }

    .scanned-codes {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
        height: 100%;
    }

    .verification-result-container {
        display: flex;
        width: 100%;
        height: 100px;
        padding-top: 10px;

        .verification-result {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #e9e9e9;
            border-radius: 5px;
        }
    }
}

.print-request-history-box {
  height: calc(100vh - 450px);
  min-height: 100px;
  overflow-y: auto;
}
</style>
